import type { SessionMetadata } from '../auth/session-store';
import { captureResponseCorrelationId } from '../observability/correlation';

export interface RefreshedAccessToken {
  token: string;
  expiresAt: number;
  session: SessionMetadata;
}

export type RefreshSessionResult =
  | ({ kind: 'authenticated' } & RefreshedAccessToken)
  | { kind: 'signed-out' }
  | { kind: 'unavailable' };

interface RefreshResponse {
  token?: unknown;
  session?: unknown;
}

function isSessionMetadata(value: unknown): value is SessionMetadata {
  if (!value || typeof value !== 'object') return false;
  const metadata = value as Record<string, unknown>;
  return (metadata.mode === 'session' || metadata.mode === 'persistent')
    && typeof metadata.expiresAt === 'string'
    && typeof metadata.recentAuthenticationValidUntil === 'string';
}

function readExpiry(token: string): number | null {
  const payload = token.split('.')[1];
  if (!payload) return null;

  try {
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(normalized)) as Record<string, unknown>;
    return typeof claims['exp'] === 'number' ? claims['exp'] : null;
  } catch {
    return null;
  }
}

/**
 * Exchanges the httpOnly session cookie for a fresh in-memory access token. The custom header is
 * the endpoint's CSRF guard; credentials remain same-origin and the cookie is never exposed to JS.
 */
export async function refreshSession(): Promise<RefreshSessionResult> {
  try {
    const origin = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
    const response = await globalThis.fetch(new URL('/v1/auth/refresh', origin), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'X-Lyno-Refresh': '1' },
    });
    captureResponseCorrelationId(response);
    if (response.status === 401) return { kind: 'signed-out' };
    if (!response.ok) return { kind: 'unavailable' };

    const body = (await response.json()) as RefreshResponse;
    // Legacy deployments may complete a rolling upgrade before they return the optional metadata.
    // Keep the access token memory-only and accept that compatible response; current APIs include it.
    if (typeof body.token !== 'string' || (body.session !== undefined && !isSessionMetadata(body.session))) {
      return { kind: 'unavailable' };
    }
    const expiresAt = readExpiry(body.token);
    return expiresAt === null
      ? { kind: 'unavailable' }
      : { kind: 'authenticated', token: body.token, expiresAt, session: body.session as SessionMetadata };
  } catch {
    return { kind: 'unavailable' };
  }
}
